import React, { useState, useEffect, useCallback, useRef } from 'react';
import Header from './Header';
import HeroSection from './HeroSection';
import MovieRow from './MovieRow';
import MovieModal from './MovieModal';
import ShareContentModal from './ShareContentModal';
import PullToRefresh from './PullToRefresh';
import { HeroSkeleton, ContentRowSkeleton } from './Skeletons';
import { contentAPI } from '../services/api';
import { mockMovies, mockCategories } from '../data/mockData';
import { useTheme } from '../context/ThemeContext';
import { Loader2 } from 'lucide-react';

const BrowsePage = () => {
  const [featuredMovie, setFeaturedMovie] = useState(null);
  const [selectedMovie, setSelectedMovie] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [realContent, setRealContent] = useState([]);
  const [userContent, setUserContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [shareContent, setShareContent] = useState(null);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const { isDark } = useTheme();
  
  const PAGE_SIZE = 20;
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Transform backend content to movie format for compatibility
  const transformContentToMovie = useCallback((content) => {
    return {
      id: content.id,
      title: content.title,
      description: content.description || 'No description available',
      poster: content.thumbnail_url || 'https://via.placeholder.com/500x750?text=No+Image',
      backdrop: content.thumbnail_url || 'https://via.placeholder.com/1920x1080?text=No+Image',
      videoId: content.file_path || content.id,
      genre: content.genre || 'General',
      year: new Date(content.created_at).getFullYear(),
      rating: 'Not Rated',
      duration: content.duration || 'N/A',
      isTrending: content.view_count > 100,
      isNetflixOriginal: content.visibility === 'public',
      realContent: true,
      contentType: content.content_type
    };
  }, []);

  const fetchContent = useCallback(async (isInitial = true) => {
    try {
      if (isInitial) {
        setLoading(true);
        setPage(1);
        setHasMore(true);
      }
      
      // Fetch public content and user's content
      const [publicResponse, userResponse] = await Promise.all([
        contentAPI.getPublicContent({ limit: PAGE_SIZE, skip: 0 }),
        contentAPI.getUserContent({ limit: 20 }).catch(() => ({ data: [] }))
      ]);
      
      const publicData = publicResponse.data || [];
      setRealContent(publicData);
      setUserContent(userResponse.data || []);
      setHasMore(publicData.length >= PAGE_SIZE);
      
      // Set featured movie - prefer real content, fallback to mock
      if (publicData.length > 0) {
        const randomContent = publicData[Math.floor(Math.random() * publicData.length)];
        setFeaturedMovie(transformContentToMovie(randomContent));
      } else {
        const randomMovie = mockMovies[Math.floor(Math.random() * mockMovies.length)];
        setFeaturedMovie(randomMovie);
      }
    } catch (error) {
      console.error('Error fetching content:', error);
      // Fallback to mock data
      const randomMovie = mockMovies[Math.floor(Math.random() * mockMovies.length)];
      setFeaturedMovie(randomMovie);
    } finally {
      setLoading(false);
    }
  }, [transformContentToMovie]);

  // Load more content for infinite scroll
  const loadMoreContent = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const skip = page * PAGE_SIZE;
      
      const response = await contentAPI.getPublicContent({ 
        limit: PAGE_SIZE, 
        skip 
      });
      
      const newContent = response.data || [];
      
      if (newContent.length > 0) {
        setRealContent(prev => [...prev, ...newContent]);
        setPage(nextPage);
        setHasMore(newContent.length >= PAGE_SIZE);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more content:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [page, loadingMore, hasMore]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (loading) return;
    
    const options = {
      root: null,
      rootMargin: '200px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !loadingMore) {
        loadMoreContent();
      }
    }, options);

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadingMore, loadMoreContent]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  // Pull-to-refresh handler
  const handleRefresh = useCallback(async () => {
    await fetchContent(true);
    // Small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));
  }, [fetchContent]);

  const handlePlay = (movie) => {
    const movieWithPlayback = {
      ...movie,
      startTime: 0
    };
    setSelectedMovie(movieWithPlayback);
    setIsModalOpen(true);
  };

  const handleAddToList = (movie) => {
    console.log('Added to list:', movie.title);
  };

  const handleOpenDetails = (movie) => {
    setSelectedMovie(movie);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMovie(null);
  };

  const handleOpenShare = (content) => {
    setShareContent(content);
    setIsShareModalOpen(true);
  };

  const handleCloseShareModal = () => {
    setIsShareModalOpen(false);
    setShareContent(null);
  };

  // Create dynamic categories based on real content
  const createCategories = useCallback(() => {
    const categories = [];
    
    // User's uploaded content
    if (userContent.length > 0) {
      categories.push({
        id: 'user-uploads',
        title: 'Your Uploads',
        movies: userContent.map(transformContentToMovie)
      });
    }
    
    // Real content from backend
    if (realContent.length > 0) {
      categories.push({
        id: 'originals',
        title: 'I Got Soul Originals',
        movies: realContent.slice(0, 15).map(transformContentToMovie)
      });
      
      const trending = realContent.filter(c => c.view_count > 50);
      if (trending.length > 0) {
        categories.push({
          id: 'trending',
          title: 'Trending Now',
          movies: trending.map(transformContentToMovie)
        });
      }
      
      categories.push({
        id: 'recent',
        title: 'Recently Added',
        movies: realContent.slice(0, 10).map(transformContentToMovie)
      });
    }
    
    if (categories.length < 3) {
      return [...categories, ...mockCategories];
    }
    
    return categories;
  }, [realContent, userContent, transformContentToMovie]);

  if (loading) {
    return (
      <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
        <Header />
        <HeroSkeleton />
        <div className="relative z-10 -mt-32 px-4 md:px-8 space-y-6">
          <ContentRowSkeleton title={true} count={6} size="large" />
          <ContentRowSkeleton title={true} count={7} />
          <ContentRowSkeleton title={true} count={7} />
        </div>
        {/* Spacer for mobile bottom nav */}
        <div className="h-20 md:h-0" />
      </div>
    );
  }

  // Render content
  const renderContent = () => (
    <>
      {featuredMovie && (
        <HeroSection
          featuredMovie={featuredMovie}
          onPlay={handlePlay}
          onOpenDetails={handleOpenDetails}
        />
      )}

      <div className={`relative z-10 -mt-32 bg-gradient-to-t ${isDark ? 'from-black via-black/90' : 'from-gray-50 via-gray-50/90'} to-transparent`}>
        <div className="pt-32 pb-8">
          {createCategories().map((category) => (
            <MovieRow
              key={category.id}
              title={category.title}
              movies={category.movies}
              onPlay={handlePlay}
              onAddToList={handleAddToList}
              onOpenDetails={handleOpenDetails}
              onOpenShare={handleOpenShare}
              size={category.title === 'I Got Soul Originals' ? 'large' : 'normal'}
            />
          ))}
          
          {/* Infinite Scroll Trigger */}
          <div 
            ref={loadMoreRef} 
            className="flex justify-center items-center py-8"
          >
            {loadingMore && (
              <div className={`flex items-center gap-3 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                <Loader2 className="w-6 h-6 animate-spin" />
                <span>Loading more content...</span>
              </div>
            )}
            {!hasMore && realContent.length > 0 && (
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                You've reached the end
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Spacer for mobile bottom nav */}
      <div className="h-20 md:h-0" />
    </>
  );

  return (
    <div className={`min-h-screen ${isDark ? 'bg-black' : 'bg-gray-50'}`}>
      <Header />
      
      {/* Pull-to-refresh only on mobile */}
      {isMobile ? (
        <PullToRefresh onRefresh={handleRefresh}>
          {renderContent()}
        </PullToRefresh>
      ) : (
        renderContent()
      )}

      {selectedMovie && (
        <MovieModal
          movie={selectedMovie}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          onPlay={handlePlay}
          onAddToList={handleAddToList}
          onOpenShare={handleOpenShare}
        />
      )}

      <ShareContentModal
        isOpen={isShareModalOpen}
        onClose={handleCloseShareModal}
        content={shareContent}
      />
    </div>
  );
};

export default BrowsePage;
